import { useEffect, useRef, useState } from "react"
import Navbar from "../components/Navbar"
import Footer from "../components/Footer"
import KeyFeatures from "../components/KeyFeatures"
import Benefits from "../components/Benefits"
import SupportedEquipmentTypes from "../components/SupportedEquipmentTypes"
import GetStartedCard from "../components/GetStartedCard"
import GetInTouch from "../components/GetInTouch"

import heroBg from "../assets/hero-bg.png"
import fuelCardImg from "../assets/fuel-card.png"
import boardDeviceImg from "../assets/Operational Efficiency.png"
import deviceImg from "../assets/costsavings.png"
import fuelDashboardImg from "../assets/Return on Investment.png"
import trackSmartImg from "../assets/support.png"

import roadRollerImg from "../assets/roadroller.png"
import concretePaverImg from "../assets/concretepaver.png"
import tandemrollerImg from "../assets/tandemroller.png"
import drumMixerImg from "../assets/drumMixer.png"
import roadRoller from "../assets/roller.png"
import motorGraderImg from "../assets/motorgrader.png"

const containerClass =
  "mx-auto w-full max-w-[1500px] px-5 sm:px-8 lg:px-12 xl:px-20"

const keyFeatures = [
  {
    title: "Advanced Fuel Tracking",
    desc: "Go far beyond traditional monitoring by optimizing every drop of fuel and reducing costs with precise tracking and analytics.",
  },
  {
    title: "Theft Prevention",
    desc: "Prevent misuse of fleet vehicles with real-time alerts, route monitoring, and strict access controls.",
  },
  {
    title: "Complete Insights",
    desc: "Gain complete visibility into vehicle performance, driver behavior, fuel usage, and trip history for smarter business decisions.",
  },
  {
    title: "Compliance Ready",
    desc: "Stay ready for inspections with automated reporting, digital fuel logs, mileage records, and accurate operational documentation.",
  },
  {
    title: "Proactive Maintenance",
    desc: "Schedule maintenance based on engine hours, fuel consumption, and real-world usage to avoid unexpected breakdowns.",
  },
  {
    title: "Sustainability Focus",
    desc: "Reduce carbon emissions, encourage responsible driving, and optimize routes to support more sustainable fleet operations.",
  },
]

const benefits = [
  {
    number: "01",
    title: "Cost Savings",
    desc: "Reduce fuel expenses by up to 30% through theft prevention, optimization, and efficient route planning.",
    image: deviceImg,
    imageAlt: "Fuel monitoring cost savings",
  },
  {
    number: "02",
    title: "Operational Efficiency",
    desc: "Streamline operations with automated fuel management, paperless reporting, and digital recordkeeping.",
    image: boardDeviceImg,
    imageAlt: "Operational efficiency through automated fuel management",
  },
  {
    number: "03",
    title: "24/7 Support",
    desc: "Dedicated technical support and customer service available around the clock to assist with any issues.",
    image: trackSmartImg,
    imageAlt: "24/7 technical support",
  },
  {
    number: "04",
    title: "Return on Investment",
    desc: "Most clients see a full return on investment within the first 3-6 months of implementation.",
    image: fuelDashboardImg,
    imageAlt: "Return on investment report",
  },
]

const equipmentItems = [
  {
    title: "16-ton Road Roller",
    image: roadRollerImg,
  },
  {
    title: "Asphalt Concrete Paver",
    image: concretePaverImg,
  },
  {
    title: "Drum Mixer",
    image: drumMixerImg,
  },
  {
    title: "Tandem Roller",
    image: tandemrollerImg,
  },
  {
    title: "CAT Motor Grader",
    image: motorGraderImg,
  },
  {
    title: "Road Roller",
    image: roadRoller,
  },
]

const howItWorksSteps = [
  {
    number: "01",
    title: "Consultation & Analysis",
    text: "We begin with a complete assessment of your fleet operations, fuel usage patterns, cost leakages, and monitoring requirements to design the right solution.",
  },
  {
    number: "02",
    title: "Device Installation",
    text: "Our team installs fuel sensors, GPS devices, and required telematics hardware across your vehicles with proper calibration and field testing.",
  },
  {
    number: "03",
    title: "Dashboard Configuration",
    text: "We configure your live dashboard, alerts, reports, users, and access controls so your team can monitor fuel, vehicles, and performance from one place.",
  },
  {
    number: "04",
    title: "Monitoring & Optimization",
    text: "After deployment, you can track fuel usage, detect theft, analyze driver behavior, reduce wastage, and continuously optimize your fleet operations.",
  },
]

  const scrollToWhyChoose = () => {
  document
    .getElementById("why-choose-solution")
    ?.scrollIntoView({ behavior: "smooth" });
};

const howItWorksCardThemes = [
  {
    card: "#ffffff",
    text: "#111111",
    muted: "rgba(17,17,17,0.72)",
    number: "#ed860f",
    border: "1px solid rgba(237,134,15,0.22)",
    shadow: "0 18px 40px rgba(0,0,0,0.08)",
  },
  {
    card: "#ed860f",
    text: "#111111",
    muted: "rgba(17,17,17,0.78)",
    number: "#ffffff",
    border: "1px solid rgba(237,134,15,0.35)",
    shadow: "0 18px 40px rgba(237,134,15,0.22)",
  },
  {
    card: "#000000",
    text: "#ffffff",
    muted: "rgba(255,255,255,0.82)",
    number: "#ed860f",
    border: "1px solid rgba(237,134,15,0.28)",
    shadow: "0 18px 40px rgba(0,0,0,0.16)",
  },
]

function HowItWorksSection() {
  const sectionRef = useRef(null)
  const cardRefs = useRef([])
  const [activeStep, setActiveStep] = useState(0)

  useEffect(() => {
    let ticking = false

    const updateActiveStep = () => {
      const checkpoint = window.innerHeight * 0.45
      let nextActive = 0

      cardRefs.current.forEach((card, index) => {
        if (!card) return

        const rect = card.getBoundingClientRect()

        if (rect.top <= checkpoint) {
          nextActive = index
        }
      })

      setActiveStep(nextActive)
      ticking = false
    }

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateActiveStep)
        ticking = true
      }
    }

    updateActiveStep()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)

    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
    }
  }, [])




  return (
    <section
      ref={sectionRef}
      className="relative overflow-visible bg-white py-14 sm:py-16 lg:py-20"
    >
      <div className={containerClass}>
        <div className="mb-10 text-center">
          <h2 className="text-3xl font-bold text-black sm:text-4xl lg:text-[42px]">
            How It Works
          </h2>

          <p className="mx-auto mt-3 max-w-[760px] text-sm leading-relaxed text-gray-600">
            Our comprehensive implementation process ensures a smooth transition
            to smarter fleet management with minimal disruption to your operations.
          </p>
        </div>

        <div className="relative mx-auto max-w-[1100px]">
          {/* left scroll indicator */}
          <div className="absolute left-[-34px] top-0 hidden h-full lg:block">
            <div className="sticky top-[260px] h-[150px] w-3 rounded-full bg-[#f5e9e9]">
              <div
                className="absolute left-1/2 h-8 w-2 -translate-x-1/2 rounded-full bg-[#ed860f] transition-all duration-500 ease-out"
                style={{
                  top: `${20 + activeStep * 36}px`,
                }}
              />
            </div>
          </div>

          <div className="space-y-10">
            {howItWorksSteps.map((step, index) => {
              const theme = howItWorksCardThemes[index % howItWorksCardThemes.length]

              return (
                <div
                  key={step.number}
                  ref={(el) => {
                    cardRefs.current[index] = el
                  }}
                  className="lg:sticky"
                  style={{
                    top: `${130 + index * 18}px`,
                    zIndex: index + 1,
                  }}
                >
                  <div
                    className={`rounded-xl px-7 py-10 transition-all duration-500 sm:px-10 lg:px-16 lg:py-14 ${activeStep === index ? "scale-[1.01]" : "scale-100"
                      }`}
                    style={{
                      background: theme.card,
                      color: theme.text,
                      border: theme.border,
                      boxShadow: activeStep === index ? theme.shadow : "0 12px 28px rgba(0,0,0,0.06)",
                    }}
                  >
                    <div
                      className="mb-5 text-[54px] font-bold leading-none sm:text-[64px]"
                      style={{ color: theme.number }}
                    >
                      {step.number}
                    </div>

                    <h3 className="text-2xl font-bold" style={{ color: theme.text }}>
                      {step.title}
                    </h3>

                    <p
                      className="mt-4 max-w-[780px] text-sm leading-relaxed sm:text-[15px]"
                      style={{ color: theme.muted }}
                    >
                      {step.text}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}

function IntroSection() {
  return (
    <section className="relative overflow-hidden bg-white py-14 sm:py-16 lg:py-20">
      <img
        src={fuelCardImg}
        alt=""
        className="absolute left-[-24px] top-20 hidden w-28 rotate-[-18deg] opacity-90 lg:block xl:w-36"
      />

      <img
        src={fuelCardImg}
        alt=""
        className="absolute right-[-24px] top-20 hidden w-28 rotate-[18deg] opacity-90 lg:block xl:w-36"
      />

      <div className={containerClass}>
        <div className="mx-auto max-w-[1120px] text-center">
          <h2 className="text-3xl font-bold leading-tight text-black sm:text-4xl lg:text-[42px]">
            Monitor Every Drop. Protect Every Mile.
          </h2>

          <p className="mx-auto mt-4 max-w-[980px] text-sm font-semibold leading-relaxed text-black sm:text-[15px]">
            The Government of India designed an Intelligent Transport System
            under the Automotive Industry Standard to ensure state-of-art
            transport infrastructure with quality, efficiency and safety.
          </p>

          <p className="mx-auto mt-4 max-w-[980px] text-sm leading-relaxed text-gray-500 sm:text-[15px]">
            The system is designed with the objective to centrally monitor all
            passenger vehicles registered in the country and smoothen traffic
            control to avoid congestions with improved safety, quality,
            efficiency, comfort, and enhanced response time in emergencies.
          </p>

          <p className="mx-auto mt-3 max-w-[980px] text-sm leading-relaxed text-gray-400">
            AIS-140 standard specifies that vehicles coming under the passenger
            category must be installed with vehicle tracking systems and
            emergency buttons approved by ARAI and ICAT. With smart monitoring,
            your fleet gains better visibility, protection, and control.
          </p>
        </div>
      </div>
    </section>
  )
}

export default function Section5() {
  return (
    <>
      {/* Hero */}
      <section className="relative mx-auto w-full max-w-[calc(100%-24px)] overflow-hidden rounded-b-[42px] bg-black sm:max-w-[calc(100%-32px)] lg:max-w-[calc(100%-48px)]">
        <Navbar />

        <div className="relative min-h-[520px] overflow-hidden sm:min-h-[600px] lg:min-h-[680px]">
          <img
            src={heroBg}
            alt="Fuel cost saving truck"
            className="absolute inset-0 h-full w-full object-cover object-center"
          />

          <div className="absolute inset-0 bg-black/45" />

          <div className="relative z-10 flex min-h-[520px] items-center justify-center px-5 pt-24 text-center sm:min-h-[600px] lg:min-h-[680px]">
            <div className="mx-auto max-w-[930px]">
              <h1 className="text-[34px] font-bold leading-tight text-white sm:text-5xl lg:text-[60px]">
                Cut Fuel Costs by Up to 30%
              </h1>

              <div className="mx-auto mt-3 h-[3px] w-[240px] rounded-full bg-[#ed860f] sm:w-[420px] lg:w-[520px]" />

              <p className="mx-auto mt-7 max-w-[780px] text-sm leading-relaxed text-white/80 sm:text-base">
                Company Name delivers accurate, real-time fuel monitoring
                solutions to prevent theft, control costs, optimize fleet
                efficiency, and drive smarter, more profitable operations.
              </p>

              <div className="mt-8 flex justify-center">
                <button
                  onClick={scrollToWhyChoose}
                  className="inline-flex min-w-[210px] items-center justify-center rounded-md bg-[#ed860f] px-8 py-4 text-sm font-medium text-white transition hover:bg-[#d9780d]"
                >
                  Get started
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>


<div id="why-choose-solution">
      <IntroSection />
</div>
  

      <KeyFeatures title="Our Key Features" features={keyFeatures} />

      <HowItWorksSection />

      <Benefits
        title="Benefits"
        desc="Our advanced fuel monitoring system offers real-time visibility into every drop of fuel consumed across your fleet. It tracks fuel levels, detects sudden drops or refueling, flags unusual usage, and helps prevent theft, reduce waste, improve vehicle efficiency, and lower operating costs."
        items={benefits}
        image={trackSmartImg}
        imageAlt="Fuel monitoring platform"
      />

      <SupportedEquipmentTypes
        title="Fuel Monitoring Can Be Installed In"
        items={equipmentItems}
      />

      <section className="bg-white py-10 sm:py-12 lg:py-16">
        <div className={containerClass}>
          <GetStartedCard
            title="Get Started in Minutes"
            desc="Experience the power of automation with FleetRobo® AIS-140 ICAT Certified Devices"
            primaryText="Explore Solutions"
            secondaryText="Contact us"
            primaryHref="/services"
            secondaryHref="/contact"
          />
        </div>
      </section>

      <Footer />
    </>
  )
}